import BroadcastEventManager from '@/js/Shared/Events/Managers/BroadcastEventManager';
import {reactive} from 'vue'
import DragItem from './Entities/DragItem';

export {DragDropControllerSingleton as default};

/**
 * Class responsible for managing access
 * and DragDrop actions.
 *
 * @author      rsommerfeld <roni.s.sommerfeld@altice.pt>
 * @copyright   Copyright (C) Sapo. All rights reserved.
 * @license     Private
 * @since       7.0.0
 */
class DragDropController {
  /**
   * Identifies drag is active
   *
   * @type {Boolean}
   */
  is_active = false;

  /**
   * Messages that will be moved and are
   * selected
   *
   * @type {DragItem}
   */
  drags = new DragItem;

  /**
   * Ref element drag overlay
   *
   * @type {HTMLElement}
   */
  drag_overlay_element;

  /**
   * Reset delay
   *
   * @type {Number}
   */
  reset_delay = 700;

  /**
   * Identify state reset
   *
   * @type {Boolean}
   */
  reset_blocked = false;

  /**
   * Events broadcaster
   *
   * @type {BroadcastEventManager}
   */
  broadcaster = new BroadcastEventManager;

  /**
   * Initiates drag start on the element
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object.<DragItem>} drag_item
   *
   * @returns {void}
   */
  onDragStart(drag_item){
    this.setDragItem(drag_item);
    this.setIsActive(true);
    this.setDragOverlayElement(this.drags.ref_overlay_element);
    this.setDragOverlayImage(this.drags.element_event);
    this.broadcaster.broadcast(this.drags.event+'-start', drag_item);
  }

  /**
   * Identifier that ends the drag
   *
   * @public
   *
   * @since 7.0.0
   *
   * @returns {void}
   */
  onDragEnd(){
    this.broadcaster.broadcast(this.drags.event+'-end');
    this.reset();
  }

  /**
   * Identifies when the drag is made to over the element
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {*} drop_data
   *
   * @returns {void}
   */
  onDragOver(drop_data, drop_suffix){
    this.drags.drop_data = drop_data;
    this.drags.drop_suffix = drop_suffix;
    this.broadcaster.broadcast(`${this.drags.event}-over-${this.drags.drop_suffix}`, this.drags);
  }

  /**
   * Identifies when the drag is made to drop the element
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {*} drop_data mixed data drop
   * @param {Array} drop_events events authorized in the drop
   * @param {String} drop_suffix zone where drop happens
   *
   * @returns {void}
   */
  onDragDrop(drop_data, drop_events, drop_suffix){
    this.drags.drop_data = drop_data;
    this.drags.drop_events = drop_events;
    this.drags.drop_suffix = drop_suffix;
    this.broadcaster.broadcast(`${this.drags.event}-drop-${this.drags.drop_suffix}`, this.drags);
  }

   /**
   * Identifies when the dragover leaves the element
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {*} drop_data mixed data drop
   * @param {Array} drop_events events authorized in the drop
   * @param {String} drop_suffix zone where drop happens
   *
   * @returns {void}
   */
  onDragLeave(drop_data, drop_events, drop_suffix){
    this.drags.drop_data = drop_data;
    this.drags.drop_events = drop_events;
    this.drags.drop_suffix = drop_suffix;
    this.broadcaster.broadcast(`${this.drags.event}-leave-${this.drags.drop_suffix}`, this.drags);
  }

  /**
   * Identifies when the dragover enter the element
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {*} drop_data mixed data drop
   * @param {Array} drop_events events authorized in the drop
   * @param {String} drop_suffix zone where drop happens
   *
   * @returns {void}
   */
  onDragEnter(drop_data, drop_events, drop_suffix){
    this.drags.drop_data = drop_data;
    this.drags.drop_events = drop_events;
    this.drags.drop_suffix = drop_suffix;
    this.broadcaster.broadcast(`${this.drags.event}-enter-${this.drags.drop_suffix}`, this.drags);
  }

  /**
   * Add the items being
   * performed drag
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {DragItem} drag_item
   *
   * @returns {void}
   */
  setDragItem(drag_item){
    this.drags = drag_item;
  }

  /**
   * Set HTMLElement Ref drag overlay
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {HTMLElement} value
   *
   * @returns {void}
   */
  setDragOverlayElement(value){
    if(!value){
      return;
    }

    this.drag_overlay_element = value;
    this.drag_overlay_element.style.left = '-9999px';
  }

  /**
   * Add mouse pointer overlay
   * containing the drag overlay component
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {DragEvent} event
   *
   * @returns {void}
   */
  setDragOverlayImage(event){
    if(!this.drag_overlay_element || !event.dataTransfer.setDragImage){
      return;
    }

    event.dataTransfer.setDragImage(this.drag_overlay_element, -10, 0);
  }

  /**
   * Set whether drag is active
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Boolean} value
   *
   * @returns {void}
   */
  setIsActive(value){
    this.is_active = value;
  }

  /**
   * Reset with delay using timeout
   *
   * @public
   *
   * @since 7.0.0
   *
   * @returns {void}
   */
  resetIn(){
    if(this.reset_blocked){
      return;
    }

    setTimeout(() => {
      this.reset_blocked = false;
      this.reset();
    }, this.reset_delay);
  }

  /**
   * Resets values.
   *
   * @public
   *
   * @since 7.0.0
   *
   * @returns {void}
   */
  reset(){
    this.drags = new DragItem;
    this.is_active = false;
  }
}

const DragDropControllerSingleton = reactive(new DragDropController);